import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { formatArabicDatePrint, formatTime12 } from '../lib/dates.js';
import { typeLabel } from '../lib/constants.js';
import { LETTERHEAD_URL } from '../api/client.js';

const DEFAULTS = {
  rowsPerPage: 6,
  padTop: 58,
  padSide: 17,
  padBottom: 31,
  signatureLabel: 'دائرة مكتب المحافظ',
  orgName: 'محافظة السويداء',
};

function toNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * The official daily schedule, rendered as A4 pages.
 *
 * Always mounted but hidden on screen; print.css reveals it and hides the
 * interface. Rendering it eagerly means the letterhead image is usually already
 * decoded by the time someone presses print.
 *
 * Rendered through a portal into <body> rather than in place. The print
 * stylesheet hides `.site-main`, and an element cannot escape an ancestor that
 * is display:none — nested here it would print blank pages. The portal makes it
 * a sibling of the application shell instead.
 */
export default function PrintDocument({ meetings, selectedDate, settings, hasLetterhead }) {
  const rowsPerPage = toNumber(settings?.print_rows_per_page, DEFAULTS.rowsPerPage);
  const padTop = toNumber(settings?.print_pad_top_mm, DEFAULTS.padTop);
  const padSide = toNumber(settings?.print_pad_side_mm, DEFAULTS.padSide);
  const padBottom = toNumber(settings?.print_pad_bottom_mm, DEFAULTS.padBottom);

  const pages = useMemo(() => {
    const chunks = [];
    for (let index = 0; index < meetings.length; index += rowsPerPage) {
      chunks.push(meetings.slice(index, index + rowsPerPage));
    }
    // A day with no appointments still prints one page, so the office can file
    // an official empty schedule.
    return chunks.length > 0 ? chunks : [[]];
  }, [meetings, rowsPerPage]);

  const contentStyle = {
    padding: hasLetterhead
      ? `${padTop}mm ${padSide}mm ${padBottom}mm`
      : '18mm 17mm 20mm',
  };

  return createPortal(
    <div id="print-root" aria-hidden="true">
      {pages.map((rows, pageIndex) => {
        const isLastPage = pageIndex === pages.length - 1;

        return (
          <section className="print-page" key={pageIndex}>
            {hasLetterhead && (
              // Repeated on every page, which reproduces the header and footer
              // artwork throughout the document.
              <img className="print-letterhead" src={LETTERHEAD_URL} alt="" />
            )}

            <div className="print-content" style={contentStyle}>
              {/* Repeated on every page, exactly as the letterhead artwork's own
                  header band would be. */}
              {!hasLetterhead && (
                <div className="print-fallback-head">
                  {settings?.print_state_name && (
                    <div className="state">{settings.print_state_name}</div>
                  )}
                  <div className="org">{settings?.org_name || DEFAULTS.orgName}</div>
                  {settings?.dept_name && <div className="dept">{settings.dept_name}</div>}
                  <div className="rule" />
                </div>
              )}

              <div className="print-pagemark">
                صفحة {pageIndex + 1} من {pages.length}
              </div>

              <div className="print-title">
                <h1>جدول مواعيد السيد المحافظ</h1>
                <div className="print-date">{formatArabicDatePrint(selectedDate)}</div>
              </div>

              {rows.length === 0 ? (
                <div className="print-empty-note">لا توجد مواعيد مسجّلة في هذا اليوم</div>
              ) : (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th className="col-time">الوقت</th>
                      <th className="col-title">الموضوع</th>
                      <th className="col-type">النوع</th>
                      <th className="col-location">المكان</th>
                      <th className="col-status">الحالة</th>
                      <th className="col-notes">ملاحظات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((meeting) => (
                      <tr key={meeting.id}>
                        <td className="cell-time">{formatTime12(meeting.time)}</td>
                        <td className="cell-title">{meeting.title}</td>
                        <td>{typeLabel(meeting.type)}</td>
                        <td className="cell-location">{meeting.location || '—'}</td>
                        <td>{meeting.status}</td>
                        <td className="cell-notes">{meeting.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* The page is RTL, so flex-start puts the signature block in the
                  lower-left corner, where an official Arabic document carries
                  it. The page marker lives at the top instead. */}
              <div className="print-foot">
                {isLastPage && (
                  <div className="print-signature">
                    <div className="signature-label">
                      {settings?.signature_label || DEFAULTS.signatureLabel}
                    </div>
                    <div className="signature-line">التوقيع والخاتم</div>
                  </div>
                )}
              </div>

              {/* Footer band, also repeated on every page. */}
              {!hasLetterhead && (
                <div className="print-fallback-foot">
                  {settings?.print_footer_text || settings?.org_name || DEFAULTS.orgName}
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>,
    document.body
  );
}
